/* eslint-disable import/prefer-default-export */
import { parseISO } from "date-fns";
import ConversationDomain, {
  AllowedMessageType,
  ConversationAnalysis,
  ConversationChannel,
  ConversationSentiment,
  ConversationUrgency,
} from "entities/domain/conversations/conversation-domain";
import { MessageStatus } from "entities/domain/conversations/message-domain";
import { AnalysisDTO, ConversationDTO } from "entities/dto/ConversationDTO";
import { contactTransformFromDtoToDomain } from "./contact-transformer";

const toConversationSentiment = (value: string): ConversationSentiment =>
  Object.values(ConversationSentiment).includes(value as ConversationSentiment)
    ? (value as ConversationSentiment)
    : ConversationSentiment.NEUTRAL;

const toConversationUrgency = (value: string): ConversationUrgency =>
  Object.values(ConversationUrgency).includes(value as ConversationUrgency)
    ? (value as ConversationUrgency)
    : ConversationUrgency.MEDIUM;

const transformAnalysis = (
  dto: AnalysisDTO | null | undefined
): ConversationAnalysis | null => {
  if (!dto) return null;

  return {
    sentiment: toConversationSentiment(dto.sentiment),
    urgency: toConversationUrgency(dto.urgency),
    summaryText: dto.summary_text,
  };
};

// API timestamps are naive UTC unless they already carry a zone.
const parseUtcIso = (value: string): Date =>
  parseISO(value.includes("Z") ? value : `${value}Z`);

// The row's time is its latest signal — the preview message or a later
// attention-raising activity (a missed inbound call) — the same rule the API
// sorts by, so client-side re-sorting agrees with the server. Creation time
// when nothing has happened yet.
const latestSignalDate = (c: ConversationDTO): Date => {
  const messageAt = c.message?.created_at
    ? parseUtcIso(c.message.created_at)
    : null;
  const attentionAt = c.last_inbound_attention_at
    ? parseUtcIso(c.last_inbound_attention_at)
    : null;
  if (messageAt && attentionAt) {
    return messageAt > attentionAt ? messageAt : attentionAt;
  }
  return messageAt ?? attentionAt ?? parseUtcIso(c.created_at);
};

export const conversationTransformFromDtoToDomain = (
  c: ConversationDTO
): ConversationDomain => {
  const activeCustomer = c.customer_channel.customers.find(
    (cust) => cust.id === c.customer_id
  );

  return new ConversationDomain(
    c.id,
    c.merchant_id,
    c.customer_id,
    c.whatsapp_group_id
      ? c.alias || "WhatsApp group"
      : activeCustomer?.full_name || c.customer_channel?.display_handle || "",
    c.customer_channel.id,
    c.alias,
    c.channel
      ? (c.channel as ConversationChannel)
      : ConversationChannel.NO_CHANNEL,
    c.is_open,
    c.created_at,
    c.unread_count,
    c.assigned_agent_id,
    // Absent on payloads from before the Inbox cutover.
    c.inbox_id ?? null,
    c.customer_channel.is_subscribed,
    c.customer_channel.is_blocked,
    c.message?.body,
    c.message?.status as MessageStatus,
    latestSignalDate(c),
    c.message?.id,
    c.allowed_messages_type === "all"
      ? AllowedMessageType.ALL
      : AllowedMessageType.TEMPLATES_ONLY,
    activeCustomer?.tag_ids || [],
    activeCustomer?.name || "",
    activeCustomer?.surname || "",
    c.message?.title ?? undefined,
    c.message?.is_incoming,
    c.assigned_ai_assistant_id || undefined,
    c.last_read_at
      ? parseISO(
          c.last_read_at.includes("Z") ? c.last_read_at : `${c.last_read_at}Z`
        )
      : null,
    (c.customer_channel.customers || []).map(contactTransformFromDtoToDomain),
    transformAnalysis(c.analysis),
    c.merchant_channel_id ?? null,
    c.whatsapp_group_id ?? null,
    c.last_inbound_attention_at
      ? parseUtcIso(c.last_inbound_attention_at)
      : null,
    c.version ?? null,
    c.is_contact_blocked ?? false,
    c.blocked_by_customer ?? null
  );
};

export const conversationsTransformFromDtoToDomain = (
  conversations: ConversationDTO[]
): ConversationDomain[] =>
  conversations.map(conversationTransformFromDtoToDomain);
